/* The generated ``FunctionSignatureOut`` type comes from a JSON-schema
 * codegen that doesn't carry full type info into TS — eslint's type-aware
 * rules see ``.name`` access as ``any`` even though the explicit interface
 * is correct. Scoped to this file because everything else is properly
 * typed (same workaround as in useExpressionAutocomplete.ts). */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/**
 * Frontend cache of the backend's ``functions.*`` registry.
 *
 * Fetches once per editor session via the dataprovider's httpClient (so auth
 * + accept-language headers come along for free). The list rarely changes
 * between page loads, so a simple module-level cache is enough; a manual
 * ``clearFunctionsCache()`` is exposed for tests.
 */

import { useEffect, useState } from 'react'
import { getJson, getUrl } from '../../../providers/data'
import type { FunctionSignatureOut } from '../../../types/apiRequests'

let cache: Promise<FunctionSignatureOut[]> | null = null

export const fetchFunctions = (): Promise<FunctionSignatureOut[]> => {
  if (cache === null) {
    cache = getJson<FunctionSignatureOut[]>(
      `${getUrl()}/workflow_execution/functions`,
    ).catch((err: unknown) => {
      // Surface to devtools so an auth-expired or 5xx isn't completely
      // invisible — the editor's only feedback otherwise is "no function
      // suggestions appear", which authors mistake for a broken feature.
      // eslint-disable-next-line no-console
      console.error('functionsRegistry: failed to load functions list', err)
      // Drop the cache so the next caller retries instead of sticking
      // with a permanent rejection.
      cache = null
      throw err
    })
  }
  return cache
}

export const clearFunctionsCache = (): void => {
  cache = null
}

export interface FunctionsRegistryState {
  /** Full signatures (with params + docs). Empty while still loading. */
  functions: FunctionSignatureOut[]
  /** Flat list of function names. ``null`` while loading or after a failed
   *  fetch — call sites use this to skip name-based validation rather than
   *  reporting bogus "unknown" errors during the loading window. */
  functionNames: string[] | null
  /** True once a fetch attempt has failed (used for the popper hint). */
  loadFailed: boolean
  /** Trigger a re-fetch. Wired into the popper/dialog "retry" buttons so
   *  authors don't have to reload the page when auth recovers. */
  retry: () => void
}

/**
 * Shared registry hook. Used by both the autocomplete popup (needs full
 * signatures for arg placeholders) and the expression validator (only
 * needs names). Multiple consumers de-dupe through the module-level
 * promise cache, so this is cheap to call from any depth.
 *
 * The hook re-fetches whenever ``retry`` is called — fetchFunctions
 * resets its cache on failure, so a fresh call after a recovered network
 * returns the real registry. Without this knob a single 5xx on page-load
 * would silently skip function-name validation for the rest of the
 * editor session.
 */
export const useFunctionsRegistry = (): FunctionsRegistryState => {
  const [functions, setFunctions] = useState<FunctionSignatureOut[] | null>(null)
  const [loadFailed, setLoadFailed] = useState<boolean>(false)
  const [retryNonce, setRetryNonce] = useState<number>(0)

  useEffect(() => {
    let cancelled = false
    fetchFunctions()
      .then((fns) => {
        if (cancelled) return
        setFunctions(fns)
        setLoadFailed(false)
      })
      .catch(() => {
        if (cancelled) return
        // Keep ``functions`` null so the popup/validator can tell loading
        // apart from "registry is genuinely empty".
        setLoadFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [retryNonce])

  return {
    functions: functions ?? [],
    functionNames: functions === null ? null : functions.map((f) => f.name),
    loadFailed,
    retry: () => setRetryNonce((n) => n + 1),
  }
}
